import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ClinicScopeProvider } from '@/contexts/ClinicScopeContext';
import { PlatformAdminProvider, usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CookieConsentProvider } from '@/contexts/CookieConsentContext';
import { CookieConsentBanner } from '@/components/cookie-consent/CookieConsentBanner';
import { UpdateAvailableBanner } from '@/components/pwa/UpdateAvailableBanner';
import { lazy, Suspense, type ReactNode, Component, type ErrorInfo } from 'react';

// Error Boundary for debugging
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            <h1 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load pages
const ContentPage = lazy(() => import('@/pages/content/ContentPage'));
const LandingPage = lazy(() => import('@/pages/landing/LandingPage'));

// Marketing pages
const MarketingLayout = lazy(() => import('@/pages/marketing/MarketingLayout'));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPage'));
const BlogPage = lazy(() => import('@/pages/marketing/BlogPage'));
const CareersPage = lazy(() => import('@/pages/marketing/CareersPage'));
const ContactPage = lazy(() => import('@/pages/marketing/ContactPage'));
const DocsPage = lazy(() => import('@/pages/marketing/DocsPage'));
const ApiReferencePage = lazy(() => import('@/pages/marketing/ApiReferencePage'));
const StatusPage = lazy(() => import('@/pages/marketing/StatusPage'));
const SupportPage = lazy(() => import('@/pages/marketing/SupportPage'));
const SecurityPage = lazy(() => import('@/pages/marketing/SecurityPage'));
const IntegrationsPage = lazy(() => import('@/pages/marketing/IntegrationsPage'));

// Environment module — local env gated, no production activation
const ENABLE_ENVIRONMENT = import.meta.env.VITE_ENABLE_ENVIRONMENT_MODULE === 'true';

// Asset Management module — local env gated (Phase G1: discovery shell)
const ENABLE_ASSET = import.meta.env.VITE_ENABLE_ASSET_MODULE === 'true';
const AssetLayout = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetLayout')) : null;

// Workflow Studio module (PR 7-Q: definition management UI).
// Build-time gate: Vite env flag. Runtime tenant gate: `module_workflow_active`
// capability (see apps/web/src/config/capabilities.ts).
const ENABLE_WORKFLOW = import.meta.env.VITE_ENABLE_WORKFLOW_MODULE === 'true';
const WorkflowLayout = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowLayout')) : null;
const WorkflowListPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowListPage')) : null;
const WorkflowTemplatesPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowTemplatesPage')) : null;
const WorkflowDefinitionPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowDefinitionPage')) : null;
// 7-R — canvas / flow designer, same ENABLE_WORKFLOW gate as 7-Q. Tree-shaken
// when the flag is off; loads lazily behind its own route chunk when on so
// @xyflow/react (~200 KB gzipped) stays out of the landing-page bundle.
const WorkflowCanvasPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowCanvasPage')) : null;
// 7-S-1 — instances UI (list + detail). Same ENABLE_WORKFLOW gate as 7-Q/7-R.
// Tree-shaken when the flag is off; loads lazily behind its own chunk when on.
const WorkflowInstancesListPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowInstancesListPage')) : null;
const WorkflowInstanceDetailPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowInstanceDetailPage')) : null;
// 7-S-2 — approvals UI (inbox + detail). Same ENABLE_WORKFLOW gate.
const WorkflowApprovalsInboxPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowApprovalsInboxPage')) : null;
const WorkflowApprovalDetailPage = ENABLE_WORKFLOW ? lazy(() => import('@/pages/workflow/WorkflowApprovalDetailPage')) : null;
const AssetDashboardPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetDashboardPage')) : null;
const AssetRegistryPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetRegistryPage')) : null;
const AssetCreatePage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetCreatePage')) : null;
const AssetEditPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetEditPage')) : null;
const AssetDetailPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetDetailPage')) : null;
const AssetCategoriesPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/AssetCategoriesPage')) : null;
const AssetSchedulesPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/ActivitySchedulesPage')) : null;
const AssetActivityLogPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/ActivityRecordsPage')) : null;
const VerificationRoundsPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/VerificationRoundsPage')) : null;
const VerificationRoundDetailPage = ENABLE_ASSET ? lazy(() => import('@/pages/asset/VerificationRoundDetailPage')) : null;
const EnvironmentLayout = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/EnvironmentLayout')) : null;
const EnvironmentDashboardPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/EnvironmentDashboardPage')) : null;
const EnvironmentIncidentsPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/IncidentsPage')) : null;
const EnvironmentPermitsPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/PermitsPage')) : null;
const EnvironmentCompliancePage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/CompliancePage')) : null;
const EnvironmentAspectsPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/AspectsPage')) : null;
const EnvironmentEnergyPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/EnergyPage')) : null;
const EnvironmentGhgPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/GhgInventoryPage')) : null;
const EnvironmentWaterPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/WaterPage')) : null;
const EnvironmentPollutionPage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/PollutionPage')) : null;
const EnvironmentWastePage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/WastePage')) : null;
const EnvironmentNaturePage = ENABLE_ENVIRONMENT ? lazy(() => import('@/pages/environment/NaturePage')) : null;

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const OnboardingPage = lazy(() => import('@/pages/auth/OnboardingPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const SsoCompletePage = lazy(() => import('@/pages/auth/SsoCompletePage'));
const SsoErrorPage = lazy(() => import('@/pages/auth/SsoErrorPage'));
const SetupWizardPage = lazy(() => import('@/pages/auth/SetupWizardPage'));
const AccessDeniedPage = lazy(() => import('@/pages/AccessDeniedPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const HubPage = lazy(() => import('@/pages/hub/HubPage'));
const MessagingPage = lazy(() => import('@/pages/messaging/MessagingPage'));
const InboxPage = lazy(() => import('@/pages/inbox/InboxPage'));
const CampusDashboardPage = lazy(() => import('@/pages/dashboard/CampusDashboardPage'));
const CampusListPage = lazy(() => import('@/pages/admin/CampusListPage'));
const BuildingListPage = lazy(() => import('@/pages/admin/BuildingListPage'));
const ZoneListPage = lazy(() => import('@/pages/admin/ZoneListPage'));
const ClinicListPage = lazy(() => import('@/pages/admin/ClinicListPage'));
const UserListPage = lazy(() => import('@/pages/admin/UserListPage'));
const GateListPage = lazy(() => import('@/pages/admin/GateListPage'));
const ScannerDevicesPage = lazy(() => import('@/pages/admin/ScannerDevicesPage'));
const StudentListPage = lazy(() => import('@/pages/admin/StudentListPage'));
const ClassListPage = lazy(() => import('@/pages/admin/ClassListPage'));
const VisitorListPage = lazy(() => import('@/pages/admin/VisitorListPage'));
const RoleCatalogPage = lazy(() => import('@/pages/admin/RoleCatalogPage'));
const AccessSimulatorPage = lazy(() => import('@/pages/admin/AccessSimulatorPage'));
const ApprovalQueuePage = lazy(() => import('@/pages/admin/ApprovalQueuePage'));
const AccessReviewsPage = lazy(() => import('@/pages/admin/AccessReviewsPage'));
const AdminAssetConfigPage = lazy(() => import('@/pages/admin/AdminAssetConfigPage'));
const MusterListPage = lazy(() => import('@/pages/muster/MusterListPage'));
const MusterSessionPage = lazy(() => import('@/pages/muster/MusterSessionPage'));
const ClassCheckinPage = lazy(() => import('@/pages/muster/ClassCheckinPage'));
const MusterHistoryPage = lazy(() => import('@/pages/muster/MusterHistoryPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const BrandingPage = lazy(() => import('@/pages/settings/BrandingPage'));
const ApiKeysPage = lazy(() => import('@/pages/settings/ApiKeysPage'));
const DsarListPage = lazy(() => import('@/pages/settings/DsarListPage'));
const DsarDetailPage = lazy(() => import('@/pages/settings/DsarDetailPage'));
const ComplianceDashboardPage = lazy(() => import('@/pages/settings/ComplianceDashboardPage'));
const WebhooksPage = lazy(() => import('@/pages/settings/WebhooksPage'));
const WebhookDetailPage = lazy(() => import('@/pages/settings/WebhookDetailPage'));
const AIUsagePage = lazy(() => import('@/pages/settings/AIUsagePage'));
const PlatformAdminCookieBannerPage = lazy(() => import('@/pages/platform-admin/PlatformAdminCookieBannerPage'));
const ProfilePage = lazy(() => import('@/pages/user/ProfilePage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const InvoicesPage = lazy(() => import('@/pages/billing/InvoicesPage'));
const UpgradePage = lazy(() => import('@/pages/billing/UpgradePage'));
const ScannerPage = lazy(() => import('@/pages/scanner/ScannerPage'));
const PreferencesPage = lazy(() => import('@/pages/user/PreferencesPage'));

// HSE pages
const HseIncidentListPage = lazy(() => import('@/pages/hse/IncidentListPage'));
const HseIncidentDetailPage = lazy(() => import('@/pages/hse/IncidentDetailPage'));
const HseNearMissListPage = lazy(() => import('@/pages/hse/NearMissListPage'));
const HseNearMissDetailPage = lazy(() => import('@/pages/hse/NearMissDetailPage'));
const HseCorrectiveActionsPage = lazy(() => import('@/pages/hse/CorrectiveActionsPage'));
const HseCapaDetailPage = lazy(() => import('@/pages/hse/HseCapaDetailPage'));
const HseSafetyBulletinsPage = lazy(() => import('@/pages/hse/SafetyBulletinsPage'));
const HseSafetyBulletinDetailPage = lazy(() => import('@/pages/hse/SafetyBulletinDetailPage'));
const HseManagementReviewsPage = lazy(() => import('@/pages/hse/ManagementReviewsPage'));
const HseManagementReviewDetailPage = lazy(() => import('@/pages/hse/ManagementReviewDetailPage'));
const HseRegulatoryReportingPage = lazy(() => import('@/pages/hse/RegulatoryReportingPage'));
const HseTaxonomyPage = lazy(() => import('@/pages/hse/TaxonomyPage'));
const HseLessonsLearnedPage = lazy(() => import('@/pages/hse/LessonsLearnedPage'));
const HseInvestigationsPage = lazy(() => import('@/pages/hse/InvestigationsPage'));
const HseInvestigationDashboardPage = lazy(() => import('@/pages/hse/InvestigationDashboardPage'));
const HseInvestigationDetailPage = lazy(() => import('@/pages/hse/InvestigationDetailPage'));

// Admin pages (additional)
const ZoneTypeListPage = lazy(() => import('@/pages/admin/ZoneTypeListPage'));
const LiveOccupancyPage = lazy(() => import('@/pages/ems/LiveOccupancyPage'));

// ERT pages
const ErtMyAssignmentsPage = lazy(() => import('@/pages/ert/MyAssignmentsPage'));
const ErtRosterManagementPage = lazy(() => import('@/pages/ert/RosterManagementPage'));

// Platform Admin pages
const PlatformAdminLoginPage = lazy(() => import('@/pages/platform-admin/PlatformAdminLoginPage'));
const PlatformAdminLayout = lazy(() => import('@/pages/platform-admin/PlatformAdminLayout'));
const PlatformAdminDashboardPage = lazy(() => import('@/pages/platform-admin/PlatformAdminDashboardPage'));
const PlatformAdminTenantsPage = lazy(() => import('@/pages/platform-admin/PlatformAdminTenantsPage'));
const PlatformAdminPlansPage = lazy(() => import('@/pages/platform-admin/PlatformAdminPlansPage'));
const PlatformAdminCmsPage = lazy(() => import('@/pages/platform-admin/cms/PlatformAdminCmsPage'));
const PlatformAdminCmsEntriesPage = lazy(() => import('@/pages/platform-admin/cms/PlatformAdminCmsEntriesPage'));
const PlatformAdminCmsEntryEditorPage = lazy(() => import('@/pages/platform-admin/cms/PlatformAdminCmsEntryEditorPage'));
const PlatformAdminCmsEntryTypesPage = lazy(() => import('@/pages/platform-admin/cms/PlatformAdminCmsEntryTypesPage'));
const PlatformAdminTenantDetailPage = lazy(() => import('@/pages/platform-admin/PlatformAdminTenantDetailPage'));
const PlatformAdminBillingPage = lazy(() => import('@/pages/platform-admin/PlatformAdminBillingPage'));
const PlatformAdminInvoicesPage = lazy(() => import('@/pages/platform-admin/PlatformAdminInvoicesPage'));
const PlatformAdminBrandingPage = lazy(() => import('@/pages/platform-admin/PlatformAdminBrandingPage'));
const PlatformAdminCompliancePage = lazy(() => import('@/pages/platform-admin/PlatformAdminCompliancePage'));
const PlatformAdminDsarOverviewPage = lazy(() => import('@/pages/platform-admin/PlatformAdminDsarOverviewPage'));
const PlatformAdminConsentAnalyticsPage = lazy(() => import('@/pages/platform-admin/PlatformAdminConsentAnalyticsPage'));
const PlatformAdminAlertRulesPage = lazy(() => import('@/pages/platform-admin/PlatformAdminAlertRulesPage'));
const PlatformAdminComplianceReportsPage = lazy(() => import('@/pages/platform-admin/PlatformAdminComplianceReportsPage'));
const PlatformAdminAlertIncidentsPage = lazy(() => import('@/pages/platform-admin/PlatformAdminAlertIncidentsPage'));
const PlatformAdminWebhooksOverviewPage = lazy(() => import('@/pages/platform-admin/PlatformAdminWebhooksOverviewPage'));
const PlatformAdminAIConfigPage = lazy(() => import('@/pages/platform-admin/PlatformAdminAIConfigPage'));

// Tenant Admin CMS pages
const CmsEntriesPage = lazy(() => import('@/pages/admin/cms/CmsEntriesPage'));
const CmsEntryEditorPage = lazy(() => import('@/pages/admin/cms/CmsEntryEditorPage'));

// Incident pages
const IncidentDashboardPage = lazy(() => import('@/pages/incident/IncidentDashboardPage'));
const IncidentListPage = lazy(() => import('@/pages/incident/IncidentListPage'));
const IncidentDetailPage = lazy(() => import('@/pages/incident/IncidentDetailPage'));
const NearMissListPage = lazy(() => import('@/pages/incident/NearMissListPage'));
const NearMissDetailPage = lazy(() => import('@/pages/incident/NearMissDetailPage'));
const CorrectiveActionsPage = lazy(() => import('@/pages/incident/CorrectiveActionsPage'));
const IncidentReportsPage = lazy(() => import('@/pages/incident/IncidentReportsPage'));
const InvestigationPage = lazy(() => import('@/pages/incident/InvestigationPage'));

// Inspection pages
const InspectionDashboardPage = lazy(() => import('@/pages/inspection/InspectionDashboardPage'));
const InspectionTemplatesPage = lazy(() => import('@/pages/inspection/InspectionTemplatesPage'));
const InspectionSchedulePage = lazy(() => import('@/pages/inspection/InspectionSchedulePage'));
const InspectionListPage = lazy(() => import('@/pages/inspection/InspectionListPage'));
const InspectionDetailPage = lazy(() => import('@/pages/inspection/InspectionDetailPage'));
const NonConformityListPage = lazy(() => import('@/pages/inspection/NonConformityListPage'));
const EquipmentRegistryPage = lazy(() => import('@/pages/inspection/EquipmentRegistryPage'));
const HSECalendarPage = lazy(() => import('@/pages/inspection/HSECalendarPage'));

// Training pages
const TrainingDashboardPage = lazy(() => import('@/pages/training/TrainingDashboardPage'));
const TrainingCatalogPage = lazy(() => import('@/pages/training/TrainingCatalogPage'));
const CourseDetailPage = lazy(() => import('@/pages/training/CourseDetailPage'));
const TrainingEnrollmentsPage = lazy(() => import('@/pages/training/TrainingEnrollmentsPage'));
const CourseLaunchPage = lazy(() => import('@/pages/training/CourseLaunchPage'));
// Training Phase 2
const TrainingCalendarPage = lazy(() => import('@/pages/training/TrainingCalendarPage'));
const TrainingSessionsPage = lazy(() => import('@/pages/training/TrainingSessionsPage'));
const SessionDetailPage = lazy(() => import('@/pages/training/SessionDetailPage'));
const TrainingAssessmentsPage = lazy(() => import('@/pages/training/TrainingAssessmentsPage'));
const AssessmentDetailPage = lazy(() => import('@/pages/training/AssessmentDetailPage'));
const OjtProgramsPage = lazy(() => import('@/pages/training/OjtProgramsPage'));
const OjtProgramDetailPage = lazy(() => import('@/pages/training/OjtProgramDetailPage'));
// Training Phase 3
const MyCertificatesPage = lazy(() => import('@/pages/training/MyCertificatesPage'));
const TrainingKpiDashboardPage = lazy(() => import('@/pages/training/TrainingKpiDashboardPage'));
const CertificateTemplatePage = lazy(() => import('@/pages/training/CertificateTemplatePage'));
// Training Phase 4
const InitiativesPage = lazy(() => import('@/pages/training/InitiativesPage'));
const InitiativeDetailPage = lazy(() => import('@/pages/training/InitiativeDetailPage'));
const TrainingRequestsPage = lazy(() => import('@/pages/training/TrainingRequestsPage'));
const TrainingRoiDashboardPage = lazy(() => import('@/pages/training/TrainingRoiDashboardPage'));
const ImpactStudyPage = lazy(() => import('@/pages/training/ImpactStudyPage'));
// Training Phase 2 — detail/runner pages
const AssessmentRunnerPage = lazy(() => import('@/pages/training/AssessmentRunnerPage'));
const OjtAssignmentDetailPage = lazy(() => import('@/pages/training/OjtAssignmentDetailPage'));
// Training Phase 3 — detail pages
const CertificateTemplateDetailPage = lazy(() => import('@/pages/training/CertificateTemplateDetailPage'));
// Training — Learning Paths + Catalog (Learner)
const CourseCatalogPage = lazy(() => import('@/pages/training/CourseCatalogPage'));
const LearningPathCatalogPage = lazy(() => import('@/pages/training/LearningPathCatalogPage'));
const LearningPathDetailPage = lazy(() => import('@/pages/training/LearningPathDetailPage'));
// Training Phase 5
const LrsClientsPage = lazy(() => import('@/pages/training/LrsClientsPage'));

// Contractor pages
const ContractorDashboardPage = lazy(() => import('@/pages/contractor/ContractorDashboardPage'));
const ContractorListPage = lazy(() => import('@/pages/contractor/ContractorListPage'));
const ContractListPage = lazy(() => import('@/pages/contractor/ContractListPage'));
const ContractorInsurancePage = lazy(() => import('@/pages/contractor/ContractorInsurancePage'));
const PrequalApplicationsPage = lazy(() => import('@/pages/contractor/PrequalApplicationsPage'));
const StopWorkOrdersPage = lazy(() => import('@/pages/contractor/StopWorkOrdersPage'));

// Audit pages
const AuditDashboardPage = lazy(() => import('@/pages/audit/AuditDashboardPage'));
const AuditListPage = lazy(() => import('@/pages/audit/AuditListPage'));
const AuditorListPage = lazy(() => import('@/pages/audit/AuditorListPage'));
const AuditFindingsPage = lazy(() => import('@/pages/audit/AuditFindingsPage'));
const AuditProgramsPage = lazy(() => import('@/pages/audit/AuditProgramsPage'));
const AuditDetailPage = lazy(() => import('@/pages/audit/AuditDetailPage'));

// Risk pages
const RiskDashboardPage = lazy(() => import('@/pages/risk/RiskDashboardPage'));
const RiskRegisterPage = lazy(() => import('@/pages/risk/RiskRegisterPage'));
const ContractorRisksPage = lazy(() => import('@/pages/risk/ContractorRisksPage'));
const TreatmentPlansPage = lazy(() => import('@/pages/risk/TreatmentPlansPage'));
const KriListPage = lazy(() => import('@/pages/risk/KriListPage'));
const RiskHeatMapPage = lazy(() => import('@/pages/risk/RiskHeatMapPage'));

// Compliance — Regulatory Register (TRP-412 A1)
const RegulatoryRegisterPage = lazy(() => import('@/pages/compliance/RegulatoryRegisterPage'));

// Event Spine — neutral "Report event or concern" intake (A2)
const EventReportPage = lazy(() => import('@/pages/events/EventReportPage'));

// Event Spine — Event Center read UI (A3.1): list + detail
const EventsPage = lazy(() => import('@/pages/events/EventsPage'));
const EventDetailPage = lazy(() => import('@/pages/events/EventDetailPage'));

// Clinic pages
const ClinicDashboardPage = lazy(() => import('@/pages/clinic/ClinicDashboardPage'));
const ClinicVisitsPage = lazy(() => import('@/pages/clinic/ClinicVisitsPage'));
const ClinicVisitDetailPage = lazy(() => import('@/pages/clinic/ClinicVisitDetailPage'));
const ClinicStudentsPage = lazy(() => import('@/pages/clinic/ClinicStudentsPage'));
const ClinicStudentProfilePage = lazy(() => import('@/pages/clinic/ClinicStudentProfilePage'));
const ClinicMedicationsPage = lazy(() => import('@/pages/clinic/ClinicMedicationsPage'));
const ClinicInventoryPage = lazy(() => import('@/pages/clinic/ClinicInventoryPage'));
const ClinicEquipmentPage = lazy(() => import('@/pages/clinic/ClinicEquipmentPage'));
const ClinicWastePage = lazy(() => import('@/pages/clinic/ClinicWastePage'));
const ClinicReportsPage = lazy(() => import('@/pages/clinic/ClinicReportsPage'));
const ClinicEncountersPage = lazy(() => import('@/pages/clinic/ClinicEncountersPage'));
const ClinicEncounterDetailPage = lazy(() => import('@/pages/clinic/ClinicEncounterDetailPage'));
const ClinicAppointmentsPage = lazy(() => import('@/pages/clinic/ClinicAppointmentsPage'));
const ClinicQueuePage = lazy(() => import('@/pages/clinic/ClinicQueuePage'));
const ClinicStaffPage = lazy(() => import('@/pages/clinic/ClinicStaffPage'));
const ClinicAutoMusterAlertsPage = lazy(() => import('@/pages/clinic/ClinicAutoMusterAlertsPage'));

// Clinic feature guard (lazy — only loaded when SoR routes are hit)
const ClinicFeatureGuard = lazy(() =>
  import('@/components/clinic/ClinicFeatureGuard').then((m) => ({ default: m.ClinicFeatureGuard })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: false, // Disable retries to prevent loops
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isBootstrapping } = useAuth();

  // Gate on `isBootstrapping` too (SESSION PR-C2b-B1b): never redirect to /login
  // while the first session bootstrap — incl. the silent refresh + recovered
  // re-probe — is still in flight, so a recoverable session never flashes login.
  if (isLoading || isBootstrapping) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isBootstrapping } = useAuth();

  // Gate on `isBootstrapping` too (SESSION PR-C2b-B1b): hold on the loading state
  // until the recovered bootstrap settles, then redirect an authenticated user
  // straight to /dashboard instead of letting /login paint.
  if (isLoading || isBootstrapping) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function LandingRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isBootstrapping } = useAuth();

  // Gate on `isBootstrapping` too (SESSION PR-C2b-B1b): don't resolve the landing
  // route until the recovered bootstrap settles.
  if (isLoading || isBootstrapping) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * OnboardingGuard: redirects to /setup if user hasn't completed onboarding.
 * Existing users are backfilled as completed (migration 128).
 * Invited users are created with onboarding_completed=true.
 */
function OnboardingGuard({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user && user.onboarding_completed === false) {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}

function PlatformAdminProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = usePlatformAdmin();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/platform-admin/login" replace />;
  }

  return <>{children}</>;
}

function PlatformAdminPublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = usePlatformAdmin();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/platform-admin" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing page - shown to unauthenticated users */}
        <Route
          path="/"
          element={
            <LandingRoute>
              <LandingPage />
            </LandingRoute>
          }
        />

        {/* Public CMS pages (cookie-policy, privacy-policy, terms, etc.) */}
        <Route path="/content/:slug" element={<ContentPage />} />

        {/* Marketing pages — public, unauthenticated */}
        <Route element={<MarketingLayout />}>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/api" element={<ApiReferencePage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
        </Route>

        {/* Onboarding - standalone page */}
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <OnboardingPage />
            </PublicRoute>
          }
        />

        {/* Post-signup wizard — authenticated, fullscreen (no AppLayout) */}
        <Route
          path="/setup"
          element={
            <ProtectedRoute>
              <SetupWizardPage />
            </ProtectedRoute>
          }
        />

        {/* Gate Scanner (device-auth, standalone) */}
        <Route path="/scanner" element={<ScannerPage />} />

        {/* Public auth routes */}
        <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* SSO routes (standalone, no layout wrapper) */}
        <Route path="/sso/complete" element={<SsoCompletePage />} />
        <Route path="/sso/error" element={<SsoErrorPage />} />

        {/* ── Operations Shell ─────────────────────────────────────────── */}
        <Route element={<ProtectedRoute><OnboardingGuard><AppLayout shell="operations" /></OnboardingGuard></ProtectedRoute>}>
          {/* Dashboard */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/hub" element={<HubPage />} />
          <Route path="/messages" element={<MessagingPage />} />
          <Route path="/campus/:id" element={<CampusDashboardPage />} />

          {/* HSE */}
          <Route path="/hse/incidents" element={<HseIncidentListPage />} />
          <Route path="/hse/incidents/:id" element={<HseIncidentDetailPage />} />
          <Route path="/hse/near-misses" element={<HseNearMissListPage />} />
          <Route path="/hse/near-misses/:id" element={<HseNearMissDetailPage />} />
          <Route path="/hse/corrective-actions" element={<HseCorrectiveActionsPage />} />
          <Route path="/hse/capas/:id" element={<HseCapaDetailPage />} />
          <Route path="/hse/safety-bulletins" element={<HseSafetyBulletinsPage />} />
          <Route path="/hse/safety-bulletins/:bulletinId" element={<HseSafetyBulletinDetailPage />} />
          <Route path="/hse/management-reviews" element={<HseManagementReviewsPage />} />
          <Route path="/hse/management-reviews/:reviewId" element={<HseManagementReviewDetailPage />} />
          <Route path="/hse/regulatory" element={<HseRegulatoryReportingPage />} />
          <Route path="/hse/taxonomy" element={<HseTaxonomyPage />} />
          <Route path="/hse/lessons-learned" element={<HseLessonsLearnedPage />} />
          <Route path="/hse/investigations" element={<HseInvestigationsPage />} />
          <Route path="/hse/investigations/dashboard" element={<HseInvestigationDashboardPage />} />
          <Route path="/hse/investigations/:id" element={<HseInvestigationDetailPage />} />

          {/* EMS — Muster */}
          <Route path="/ems/muster" element={<MusterListPage />} />
          <Route path="/ems/muster/history" element={<MusterHistoryPage />} />
          <Route path="/ems/muster/:sessionId" element={<MusterSessionPage />} />
          <Route path="/ems/muster/:sessionId/class/:classId" element={<ClassCheckinPage />} />

          {/* EMS — Live Occupancy */}
          <Route path="/ems/live-occupancy" element={<LiveOccupancyPage />} />

          {/* EMS — ERT */}
          <Route path="/ems/ert/assignments" element={<ErtMyAssignmentsPage />} />
          <Route path="/ems/ert/roster" element={<ErtRosterManagementPage />} />

          {/* Clinic — wrapped in ClinicScopeProvider for per-clinic data scoping */}
          <Route element={<ClinicScopeProvider />}>
            <Route path="/clinic" element={<ClinicDashboardPage />} />
            <Route path="/clinic/encounters" element={<ClinicFeatureGuard flag="clinic_sor_schema"><ClinicEncountersPage /></ClinicFeatureGuard>} />
            <Route path="/clinic/encounters/:id" element={<ClinicFeatureGuard flag="clinic_sor_schema"><ClinicEncounterDetailPage /></ClinicFeatureGuard>} />
            <Route path="/clinic/visits" element={<ClinicVisitsPage />} />
            <Route path="/clinic/visits/:id" element={<ClinicVisitDetailPage />} />
            <Route path="/clinic/appointments" element={<ClinicFeatureGuard flag="clinic_sor_schema"><ClinicAppointmentsPage /></ClinicFeatureGuard>} />
            <Route path="/clinic/queue" element={<ClinicFeatureGuard flag="clinic_sor_schema"><ClinicQueuePage /></ClinicFeatureGuard>} />
            <Route path="/clinic/students" element={<ClinicStudentsPage />} />
            <Route path="/clinic/students/:id" element={<ClinicStudentProfilePage />} />
            <Route path="/clinic/medications" element={<ClinicMedicationsPage />} />
            <Route path="/clinic/inventory" element={<ClinicInventoryPage />} />
            <Route path="/clinic/equipment" element={<ClinicEquipmentPage />} />
            <Route path="/clinic/waste" element={<ClinicWastePage />} />
            <Route path="/clinic/reports" element={<ClinicReportsPage />} />
            <Route path="/clinic/staff" element={<ClinicFeatureGuard flag="clinic_sor_schema"><ClinicStaffPage /></ClinicFeatureGuard>} />
            <Route path="/clinic/auto-muster" element={<ClinicFeatureGuard flag="auto_muster_on_emergency_visit"><ClinicAutoMusterAlertsPage /></ClinicFeatureGuard>} />
          </Route>

          {/* Incident Management */}
          <Route path="/incident" element={<IncidentDashboardPage />} />
          <Route path="/incident/incidents" element={<IncidentListPage />} />
          <Route path="/incident/incidents/:id" element={<IncidentDetailPage />} />
          <Route path="/incident/incidents/:id/investigation" element={<InvestigationPage />} />
          <Route path="/incident/near-misses" element={<NearMissListPage />} />
          <Route path="/incident/near-misses/:id" element={<NearMissDetailPage />} />
          <Route path="/incident/corrective-actions" element={<CorrectiveActionsPage />} />
          <Route path="/incident/reports" element={<IncidentReportsPage />} />

          {/* Inspections & Audits */}
          <Route path="/inspection" element={<InspectionDashboardPage />} />
          <Route path="/inspection/templates" element={<InspectionTemplatesPage />} />
          <Route path="/inspection/schedule" element={<InspectionSchedulePage />} />
          <Route path="/inspection/list" element={<InspectionListPage />} />
          <Route path="/inspection/inspections/:id" element={<InspectionDetailPage />} />
          <Route path="/inspection/non-conformities" element={<NonConformityListPage />} />
          <Route path="/inspection/equipment" element={<EquipmentRegistryPage />} />
          <Route path="/inspection/calendar" element={<HSECalendarPage />} />

          {/* Training */}
          <Route path="/training" element={<TrainingDashboardPage />} />
          <Route path="/training/catalog" element={<CourseCatalogPage />} />
          <Route path="/training/learning-paths" element={<LearningPathCatalogPage />} />
          <Route path="/training/learning-paths/:id" element={<LearningPathDetailPage />} />
          <Route path="/training/courses" element={<TrainingCatalogPage />} />
          <Route path="/training/courses/:id" element={<CourseDetailPage />} />
          <Route path="/training/enrollments" element={<TrainingEnrollmentsPage />} />
          <Route path="/training/launch/:enrollmentId" element={<CourseLaunchPage />} />
          {/* Training Phase 2 */}
          <Route path="/training/calendar" element={<TrainingCalendarPage />} />
          <Route path="/training/sessions" element={<TrainingSessionsPage />} />
          <Route path="/training/sessions/:sessionId" element={<SessionDetailPage />} />
          <Route path="/training/assessments" element={<TrainingAssessmentsPage />} />
          <Route path="/training/assessments/:assessmentId" element={<AssessmentDetailPage />} />
          <Route path="/training/ojt" element={<OjtProgramsPage />} />
          <Route path="/training/ojt/:programId" element={<OjtProgramDetailPage />} />
          <Route path="/training/assessments/:assessmentId/attempt" element={<AssessmentRunnerPage />} />
          <Route path="/training/ojt/assignments/:assignmentId" element={<OjtAssignmentDetailPage />} />
          {/* Training Phase 3 */}
          <Route path="/training/certificates" element={<MyCertificatesPage />} />
          <Route path="/training/certificates/templates" element={<CertificateTemplatePage />} />
          <Route path="/training/certificates/templates/:templateId" element={<CertificateTemplateDetailPage />} />
          <Route path="/training/kpis" element={<TrainingKpiDashboardPage />} />
          {/* Training Phase 4 */}
          <Route path="/training/initiatives" element={<InitiativesPage />} />
          <Route path="/training/initiatives/:initiativeId" element={<InitiativeDetailPage />} />
          <Route path="/training/requests" element={<TrainingRequestsPage />} />
          <Route path="/training/roi" element={<TrainingRoiDashboardPage />} />
          <Route path="/training/roi/:studyId" element={<ImpactStudyPage />} />
          {/* Training Phase 5 */}
          <Route path="/training/lrs" element={<LrsClientsPage />} />

          {/* Contractor Management */}
          <Route path="/contractor" element={<ContractorDashboardPage />} />
          <Route path="/contractor/list" element={<ContractorListPage />} />
          <Route path="/contractor/contracts" element={<ContractListPage />} />
          <Route path="/contractor/insurance" element={<ContractorInsurancePage />} />
          <Route path="/contractor/prequal" element={<PrequalApplicationsPage />} />
          <Route path="/contractor/stop-work" element={<StopWorkOrdersPage />} />

          {/* Audit Management */}
          <Route path="/audit" element={<AuditDashboardPage />} />
          <Route path="/audit/list" element={<AuditListPage />} />
          <Route path="/audit/programs" element={<AuditProgramsPage />} />
          <Route path="/audit/findings" element={<AuditFindingsPage />} />
          <Route path="/audit/auditors" element={<AuditorListPage />} />
              <Route path="/audit/audits/:id" element={<AuditDetailPage />} />

          {/* Risk Management */}
          <Route path="/risk" element={<RiskDashboardPage />} />
          <Route path="/risk/register" element={<RiskRegisterPage />} />
          <Route path="/risk/contractor" element={<ContractorRisksPage />} />
          <Route path="/risk/treatments" element={<TreatmentPlansPage />} />
          <Route path="/risk/kris" element={<KriListPage />} />
          <Route path="/risk/heatmap" element={<RiskHeatMapPage />} />

          {/* Regulatory Register (TRP-412 A1) — tenant-user read-only view of
              the Acceptance Ledger projection. Gated by
              catalog:tenant_regulatory_register:read at the nav layer and
              authoritatively at the backend by authorizePermission. */}
          <Route path="/compliance/regulatory-register" element={<RegulatoryRegisterPage />} />

          {/* Event Spine — neutral "Report event or concern" intake (A2).
              Submits to the production-live A1 API POST /api/v1/ops/events,
              gated at the nav layer by ops:event:report and authoritatively at
              the backend by authorizePermission. */}
          <Route path="/events/report" element={<EventReportPage />} />

          {/* Event Spine — Event Center read UI (A3.1): neutral list + detail
              over the live A1 read APIs (GET /api/v1/ops/events[/:id]). Gated by
              ops:event:view at the nav layer and authoritatively at the backend. */}
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />

          {/* Environment — local env gated, route-only (no nav entry) */}
          {ENABLE_ENVIRONMENT && EnvironmentLayout && EnvironmentDashboardPage && EnvironmentIncidentsPage && EnvironmentPermitsPage && EnvironmentCompliancePage && EnvironmentAspectsPage && EnvironmentEnergyPage && EnvironmentGhgPage && EnvironmentWaterPage && EnvironmentPollutionPage && EnvironmentWastePage && EnvironmentNaturePage && (
            <Route path="/environment" element={<EnvironmentLayout />}>
              <Route index element={<EnvironmentDashboardPage />} />
              <Route path="incidents" element={<EnvironmentIncidentsPage />} />
              <Route path="permits" element={<EnvironmentPermitsPage />} />
              <Route path="compliance" element={<EnvironmentCompliancePage />} />
              <Route path="aspects" element={<EnvironmentAspectsPage />} />
              <Route path="energy" element={<EnvironmentEnergyPage />} />
              <Route path="ghg" element={<EnvironmentGhgPage />} />
              <Route path="water" element={<EnvironmentWaterPage />} />
              <Route path="pollution" element={<EnvironmentPollutionPage />} />
              <Route path="waste" element={<EnvironmentWastePage />} />
              <Route path="nature" element={<EnvironmentNaturePage />} />
            </Route>
          )}

          {/* Workflow Studio (PR 7-Q + 7-R) — flag-gated. Execution UI,
              approvals UI, template browser, evidence, triggers-UI are
              deferred to 7-S / 7-T / 7-U / future slices. */}
          {ENABLE_WORKFLOW &&
            WorkflowLayout &&
            WorkflowListPage &&
            WorkflowDefinitionPage &&
            WorkflowCanvasPage &&
            WorkflowInstancesListPage &&
            WorkflowInstanceDetailPage &&
            WorkflowApprovalsInboxPage &&
            WorkflowApprovalDetailPage && (
              <Route path="/workflow" element={<WorkflowLayout />}>
                <Route index element={<Navigate to="/workflow/definitions" replace />} />
                <Route path="definitions" element={<WorkflowListPage />} />
                {/* 7-T templates browser */}
                {WorkflowTemplatesPage ? (
                  <Route path="templates" element={<WorkflowTemplatesPage />} />
                ) : null}
                <Route path="definitions/:id" element={<WorkflowDefinitionPage />} />
                {/* PR 7-R canvas route */}
                <Route
                  path="definitions/:id/versions/:vid/canvas"
                  element={<WorkflowCanvasPage />}
                />
                {/* PR 7-S-1 instances routes */}
                <Route path="instances" element={<WorkflowInstancesListPage />} />
                <Route path="instances/:instanceId" element={<WorkflowInstanceDetailPage />} />
                {/* PR 7-S-2 approvals routes */}
                <Route path="approvals" element={<WorkflowApprovalsInboxPage />} />
                <Route path="approvals/:approvalId" element={<WorkflowApprovalDetailPage />} />
              </Route>
            )}

          {/* Asset Management — local env gated (Phase G1: discovery shell) */}
          {ENABLE_ASSET && AssetLayout && AssetDashboardPage && AssetRegistryPage && AssetCreatePage && AssetEditPage && AssetDetailPage && AssetCategoriesPage && AssetSchedulesPage && AssetActivityLogPage && VerificationRoundsPage && VerificationRoundDetailPage && (
            <Route path="/assets" element={<AssetLayout />}>
              <Route index element={<AssetDashboardPage />} />
              <Route path="registry" element={<AssetRegistryPage />} />
              {/* 5I: /registry/new must come before /registry/:id so "new" is not matched as an id. */}
              <Route path="registry/new" element={<AssetCreatePage />} />
              <Route path="registry/:id/edit" element={<AssetEditPage />} />
              <Route path="registry/:id" element={<AssetDetailPage />} />
              <Route path="categories" element={<AssetCategoriesPage />} />
              <Route path="schedules" element={<AssetSchedulesPage />} />
              <Route path="activity" element={<AssetActivityLogPage />} />
              <Route path="verification" element={<VerificationRoundsPage />} />
              <Route path="verification/:roundId" element={<VerificationRoundDetailPage />} />
            </Route>
          )}

          {/* User */}
          <Route path="/user/preferences" element={<PreferencesPage />} />
          <Route path="/user/profile" element={<ProfilePage />} />
        </Route>

        {/* ── Admin Console Shell ────────────────────────────────────────── */}
        <Route element={<ProtectedRoute><OnboardingGuard><AppLayout shell="admin" /></OnboardingGuard></ProtectedRoute>}>
          <Route path="/admin/campuses" element={<CampusListPage />} />
          <Route path="/admin/buildings" element={<BuildingListPage />} />
          <Route path="/admin/zones" element={<ZoneListPage />} />
          <Route path="/admin/clinics" element={<ClinicListPage />} />
          <Route path="/admin/gates" element={<GateListPage />} />
          <Route path="/admin/users" element={<UserListPage />} />
          <Route path="/admin/scanner-devices" element={<ScannerDevicesPage />} />
          <Route path="/admin/students" element={<StudentListPage />} />
          <Route path="/admin/classes" element={<ClassListPage />} />
          <Route path="/admin/visitors" element={<VisitorListPage />} />
          <Route path="/admin/zone-types" element={<ZoneTypeListPage />} />
          <Route path="/admin/roles" element={<RoleCatalogPage />} />
          <Route path="/admin/access-simulator" element={<AccessSimulatorPage />} />
          <Route path="/admin/approvals" element={<ApprovalQueuePage />} />
          <Route path="/admin/access-reviews" element={<AccessReviewsPage />} />
              <Route path="/admin/asset-config" element={<AdminAssetConfigPage />} />
          {/* Tenant Admin CMS */}
          <Route path="/admin/cms" element={<CmsEntriesPage />} />
          <Route path="/admin/cms/entries" element={<CmsEntriesPage />} />
          <Route path="/admin/cms/entries/:id" element={<CmsEntryEditorPage />} />

          {/* Settings (tenant admin config) */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/profile" element={<Navigate to="/user/profile" replace />} />
          <Route path="/settings/branding" element={<BrandingPage />} />
          <Route path="/settings/api-keys" element={<ApiKeysPage />} />
          <Route path="/settings/compliance" element={<ComplianceDashboardPage />} />
          <Route path="/settings/compliance/dsar" element={<DsarListPage />} />
          <Route path="/settings/compliance/dsar/:id" element={<DsarDetailPage />} />
          <Route path="/settings/ai-usage" element={<AIUsagePage />} />
          <Route path="/settings/webhooks" element={<WebhooksPage />} />
          <Route path="/settings/webhooks/:id" element={<WebhookDetailPage />} />

          {/* Billing (tenant admin) */}
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/invoices" element={<InvoicesPage />} />
          <Route path="/billing/upgrade" element={<UpgradePage />} />
        </Route>

        {/* Access Denied — standalone (no shell layout) */}
        <Route path="/access-denied" element={<ProtectedRoute><AccessDeniedPage /></ProtectedRoute>} />

        {/* Platform Admin Routes - wrapped in their own provider */}
        <Route
          path="/platform-admin/login"
          element={
            <PlatformAdminProvider>
              <PlatformAdminPublicRoute>
                <PlatformAdminLoginPage />
              </PlatformAdminPublicRoute>
            </PlatformAdminProvider>
          }
        />
        <Route
          path="/platform-admin/*"
          element={
            <PlatformAdminProvider>
              <PlatformAdminProtectedRoute>
                <Routes>
                  <Route element={<PlatformAdminLayout />}>
                    <Route index element={<PlatformAdminDashboardPage />} />
                    <Route path="tenants" element={<PlatformAdminTenantsPage />} />
                    <Route path="tenants/:id" element={<PlatformAdminTenantDetailPage />} />
                    <Route path="billing" element={<PlatformAdminBillingPage />} />
                    <Route path="billing/invoices" element={<PlatformAdminInvoicesPage />} />
                    <Route path="plans" element={<PlatformAdminPlansPage />} />
                    <Route path="cms" element={<PlatformAdminCmsPage />} />
                    <Route path="cms/entries" element={<PlatformAdminCmsEntriesPage />} />
                    <Route path="cms/entries/:id" element={<PlatformAdminCmsEntryEditorPage />} />
                    <Route path="cms/types" element={<PlatformAdminCmsEntryTypesPage />} />
                    <Route path="inbox" element={<InboxPage />} />
                    <Route path="branding" element={<PlatformAdminBrandingPage />} />
                    <Route path="compliance" element={<PlatformAdminCompliancePage />} />
                    <Route path="compliance/consent" element={<PlatformAdminConsentAnalyticsPage />} />
                    <Route path="compliance/alerts" element={<PlatformAdminAlertRulesPage />} />
                    <Route path="compliance/reports" element={<PlatformAdminComplianceReportsPage />} />
                    <Route path="compliance/incidents" element={<PlatformAdminAlertIncidentsPage />} />
                    <Route path="compliance/dsar" element={<PlatformAdminDsarOverviewPage />} />
                    <Route path="webhooks" element={<PlatformAdminWebhooksOverviewPage />} />
                    <Route path="ai-config" element={<PlatformAdminAIConfigPage />} />
                    <Route path="cookie-banner" element={<PlatformAdminCookieBannerPage />} />
                  </Route>
                </Routes>
              </PlatformAdminProtectedRoute>
            </PlatformAdminProvider>
          }
        />

        {/* Catch all - redirect to dashboard for authenticated, landing for others */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CookieConsentProvider>
          <AuthProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster
              position="top-right"
              richColors
              duration={4000}
              closeButton
            />
          </AuthProvider>
          <CookieConsentBanner />
          <UpdateAvailableBanner />
        </CookieConsentProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
